import React, { useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField
} from '@mui/material'
import {
  type NewOrganizationInformation,
  type OrganizationBasicInformation,
  searchAllOrganizationsByName,
  searchOrganizationsByName
} from 'services'
import { type OrgFilters } from 'components'

export interface OrgParams {
  title: string
  id: number
  countryId: number | null
}

interface ParentOrgProps {
  onChange: (o: OrgParams[]) => void
  filters: OrgFilters | OrganizationBasicInformation | NewOrganizationInformation
  allOrgs?: boolean
  disabled?: boolean
}

export const ParentOrgSelect: React.FC<ParentOrgProps> = ({ filters, onChange, allOrgs = true, disabled = false }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [options, setOptions] = useState<OrgParams[]>([])
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()
  const [textShowNoOptions, setTextShowNoOptions] = useState<string>('You must enter with id or names to display parent org.')

  const getData = async (searchTerm: string, countryId: number | null): Promise<void> => {
    setLoading(true)
    const parentOrgIdsAlreadyAdded = allOrgs
      ? (filters as OrgFilters).parent_orgs.map((parentOrg: { id: any }) => parentOrg.id)
      : []

    try {
      const value = await (allOrgs ? searchOrganizationsByName(searchTerm, countryId, parentOrgIdsAlreadyAdded) : searchAllOrganizationsByName(searchTerm, countryId, parentOrgIdsAlreadyAdded))
      if (value === null || value?.length === 0) {
        setTextShowNoOptions('The value could not be found. Please try again.')
      }
      setOptions(value ?? null)
      setLoading(false)
    } catch (reason) {
      console.log(reason)
    }
  }
  const getOrgValue = (): OrgParams | OrgParams[] | null => {
    if (allOrgs) {
      return (filters as OrgFilters).parent_orgs
    }
    if ('parentOrganization' in filters) {
      return (filters).parentOrganization
    }
    return null
  }

  const getCountryIdIfExisting = (): number | null => {
    if (allOrgs) {
      return (filters as OrgFilters).country
    } else {
      return (filters as OrganizationBasicInformation).country?.id ?? null
    }
  }
  const onInputChange = (
    event: React.ChangeEvent<unknown>,
    value: string
  ): void => {
    if (options.find((value1) => value1.title === value) == null) {
      if ((value !== '') && (value.length >= 3 || !isNaN(Number(value)))) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          const countryId = getCountryIdIfExisting()
          getData(value, countryId).catch(reason => { console.log(reason) })
        }, 500)
      } else {
        setOptions([])
      }
    } else {
      setOptions([])
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
      <Autocomplete
        multiple={allOrgs}
        noOptionsText={textShowNoOptions}
        id="combo-box-demo"
        options={options}
        value={getOrgValue()}
        disabled={disabled}
        onInputChange={onInputChange}
        getOptionLabel={(option) => option.title}
        style={{ width: '100%' }}
        isOptionEqualToValue={(option, value) =>
          option.id.toString() === value?.id.toString()
        }
        onChange={(event, value: OrgParams | OrgParams[] | null) => {
          const selectedOptions = Array.isArray(value) ? value : ((value != null) ? [value] : [])
          onChange(selectedOptions)
        }}
        renderInput={(params) => (
          <div>
            <TextField
              {...params}
              placeholder={textShowNoOptions}
              label='Select a Parent Organization'
              variant="outlined"
            />

          </div>
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
            {...props}
            key={option.id}
          >
            {option.title}
          </Box>
        )}
      />
      {loading && <CircularProgress sx={{ alignSelf: 'center' }} />}
    </Box>
  )
}
